import React, { useState, useEffect } from 'react';
import { Table, Badge, Alert, Spinner, Card, Button } from 'react-bootstrap';
import { getUserQuestions } from '../services/questionService';
import { formatDateTime } from '../utils/dateUtils';
import './UserQuestions.scss';

const UserQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const userQuestions = await getUserQuestions();
      setQuestions(userQuestions);
      setError('');
    } catch (err) {
      setError('Failed to load your questions. Please try again later.');
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'answered':
        return <Badge bg="success">Answered</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      case 'pending':
      default:
        return <Badge bg="warning" text="dark">Pending</Badge>;
    }
  };

  const toggleExpandQuestion = (questionId) => {
    if (expandedQuestion === questionId) {
      setExpandedQuestion(null);
    } else {
      setExpandedQuestion(questionId);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (questions.length === 0) {
    return (
      <div className="user-questions">
        <Alert variant="info">
          You haven't asked any questions yet. Go to the question form to ask your first question!
        </Alert>
      </div>
    );
  }

  return (
    <div className="user-questions">
      <div className="questions-list">
        <Table responsive striped hover>
          <thead>
            <tr>
              <th>Title</th>
              <th>Track</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {questions.map((question) => (
              <tr key={question.questionId} onClick={() => toggleExpandQuestion(question.questionId)}>
                <td>{question.title}</td>
                <td>
                  <Badge bg="secondary">{question.track}</Badge>
                </td>
                <td>{formatDateTime(question.timestamp)}</td>
                <td>{getStatusBadge(question.status)}</td>
                <td>
                  <Button variant="link" size="sm">
                    {expandedQuestion === question.questionId ? 'Collapse' : 'Expand'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        
        {questions.map((question) => (
          expandedQuestion === question.questionId && (
            <Card key={`detail-${question.questionId}`} className="mb-3">
              <Card.Body>
                <div className="question-content mb-3">
                  <h6>Your Question:</h6>
                  <p>{question.question}</p>
                </div>
                
                {question.answer && (
                  <div className="answer-content">
                    <h6>Answer:</h6>
                    <p>{question.answer}</p>
                    <div className="answer-meta">
                      <small className="text-muted">
                        Answered on {formatDateTime(question.answerTimestamp)}
                      </small>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )
        ))}
      </div>
    </div>
  );
};

export default UserQuestions;
